<template #default>
  <div class="page-view">
    <a-card class="page-filter" :bordered="false">
      <a-form
        layout="horizontal"
        :label-col="labelCol"
        :wrapper-col="wrapperCol"
        @submit.prevent="onSearch"
      >
        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Name">
              <a-input
                v-model:value="query.displayCode"
                @pressEnter="onSearch"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Currency">
              <a-select
                v-model:value="query.pnlCurrency"
                allow-clear
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in currencyList"
                  :key="item.currencyCode"
                  :value="item.currencyCode"
                >
                  {{ item.currencyCode }} - {{ item.currencyDesc }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Type">
              <a-select
                v-model:value="query.typeCode"
                style="width: 100%"
                allow-clear
                placeholder="All"
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in typeCodeOptions"
                  :key="item"
                  :value="item"
                >
                  {{ item }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Term">
              <a-select
                v-model:value="query.termCode"
                style="width: 100%"
                allow-clear
                placeholder="All"
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in termCodeOptions"
                  :key="item"
                  :value="item"
                >
                  {{ item }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Identifier Type">
              <a-select
                v-model:value="query.identType"
                allow-clear
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in identifierTypeList"
                  :key="item"
                  :value="item"
                >
                  {{ item }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Identifier">
              <a-auto-complete
                v-model:value="query.identifier"
                :options="tickerOptions"
                @search="onSearchTicker"
                @select="onSearchTicker"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item
              :colon="false"
              :labelCol="{ span: 6, style: { textAlign: 'left' } }"
              :wrapperCol="{ span: 18 }"
            >
              <div class="page-actions">
                <a-button type="primary" @click="onSearch" :loading="loading"
                  >Search</a-button
                >
                <a-button @click="showAddModal">
                  <PlusOutlined />
                  Add
                </a-button>
                <a-button @click="showCopyModal">Copy</a-button>
              </div>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <div class="page-grid page-grid--h490">
      <AgGridVue
        style="width: 100%; height: 100%"
        :theme="themeBalham"
        :loading="loading"
        :gridOptions="gridOptions"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="rowData"
      />
    </div>
    <IdentifierView
      v-model:visible="identifierModalVisible"
      :rawData="identifierRawData"
      @update:identifiers="handleUpdateIdentifiers"
    />
    <AddEditInterestRateView
      v-model:visible="addBasketIndexModalVisible"
      :isEdit="isEdit"
      :addForm="addForm"
      @update:security="handleUpdateSecurity"
    />
  </div>
</template>

<script setup>
import { ref, defineExpose, onMounted, provide, computed } from 'vue'
import { useStore } from 'vuex'
import { themeBalham } from 'ag-grid-community'

import { message } from 'ant-design-vue'
import { PlusOutlined } from '@ant-design/icons-vue'

import { buildInterestRateColumnDefs } from './InterestRateView_ColumnDefs'
import AgEditCellRenderer from '@/components/AgEditCellRenderer.vue'
import AddEditInterestRateView from './AddEditInterestRateView.vue'
import IdentifierView from '../IdentifierView.vue'

import reference from '@/api/reference'
import security from '@/api/security'
import dayjs from 'dayjs'
import { mapIdentifierField } from '@/utils/secutils'
import debounce from 'lodash/debounce'

const store = useStore()
const gridApi = ref(null)
const gridOptions = {
  rowSelection: 'single',

  onGridReady: (params) => {
    gridApi.value = params.api
  },
}

const defaultColDef = {
  filter: true,
  sortable: true,
  resizable: true,
}

defineExpose({
  AgEditCellRenderer,
})

const currencyList = computed(() => {
  const currencies = store.getters['reference/getCurrencyList'] || []
  return [...currencies].sort((a, b) =>
    a.currencyCode.localeCompare(b.currencyCode)
  )
})

const labelCol = {
  span: 6,
  style: { textAlign: 'left' },
}

const wrapperCol = {
  span: 18,
}

const loading = ref(false)
const rowData = ref([])
const typeCodeOptions = ref([
  'CALENDING',
  'CD_RATE',
  'CNHHIBOR',
  'EURIBOR',
  'EURIBOR A/365',
  'HIBOR',
  'JPTONAR',
  'LIBOR',
  'MANUAL_FINANCING',
  'NIBOR',
  'RBAINTERBANK',
  'REPO_RATE',
  'SGD_OIS_SORA',
  'SIBOR',
  'SOFR_RATE',
  'SONION',
  'SOR',
  'SSARON',
  'STIB',
  'SWAP_RATE',
  'SWAP_RATE_ALT',
  'ZERO_RATE',
])
const termCodeOptions = ref([
  '10YEAR',
  '1MONTH',
  '1WEEK',
  '1YEAR',
  '2MONTH',
  '2WEEK',
  '2YEAR',
  '3MONTH',
  '3YEAR',
  '4MONTH',
  '4WEEK',
  '4YEAR',
  '5MONTH',
  '5YEAR',
  '6MONTH',
  '6YEAR',
  '7MONTH',
  '7YEAR',
  '8YEAR',
  '9MONTH',
  '9YEAR',
  'O/N',
  'OVERNIGHT',
  'S/N',
  'SPOT',
  'T/N',
])

const exchangeList = ref([])
const identifierTypeList = ref([])
const tickerOptions = ref([])
const tickerList = ref([])
const identifierRawData = ref({
  securityId: null,
  identifiers: [],
})
const addBasketIndexModalVisible = ref(false)
const addForm = ref({})
const isEdit = ref(false)
const identifierModalVisible = ref(false)

const query = ref({
  externalInstClass: null,
  exchangeCode: null,
  displayCode: null,
  pnlCurrency: null,
  typeCode: null,
  termCode: null,
  identType: null,
  identifier: null,
})

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback

const searchTicker = (ticker) => {
  if (!ticker || ticker.length < 3 || !query.value.identType) {
    return
  }
  const identType = query.value.identType
  security
    .getSimpleSecurities({
      tickerOperator: 'START_WITH',
      identType,
      identifier: ticker,
    })
    .then((res) => {
      const data = Array.isArray(res.data) ? res.data : []
      tickerList.value = data
      const tickers = data
        .filter((item) => mapIdentifierField(item, identType))
        .map((item) => mapIdentifierField(item, identType))
      tickers.sort((a, b) => b.localeCompare(a))
      const arr = tickers.length > 8 ? tickers.slice(0, 8) : tickers
      tickerOptions.value = arr.map((item) => ({ value: item }))
    })
}

const onSearchTicker = debounce(
  function (ticker) {
    searchTicker(ticker)
  },
  600,
  { maxWait: 1000 }
)

const onSearch = async () => {
  loading.value = true
  try {
    const params = { ...query.value }
    params.assetClass = 'RATE'
    if (!params.identifier || !params.identifier.trim()) {
      params.identType = null
    }
    params.tickerOperator = 'START_WITH'

    const resp = await security.getSimpleSecurities(params)
    const data = resp.data
    rowData.value = Array.isArray(data) ? data : []
    if (rowData.value.length === 0) {
      message.info('No data found.')
    }
  } catch (e) {
    message.error(getErrorMessage(e, 'Query failed'))
  } finally {
    loading.value = false
  }
}

const showAddModal = () => {
  isEdit.value = false
  addForm.value = {
    securityId: null,
    externalSecurityId: null,
    assetClass: 'RATE',
    issueDate: null,
    settleDate: null,
    maturityDate: null,
    exchange: {
      exchangeId: null,
      exchName: null,
      micExchName: null,
    },
    issuer: {
      issuerId: null,
      shortName: null,
      name: null,
    },
    underlyingTicker: null,
    instrumentClass: 'RATE',
    isin: null,
    ticker: null,
    name: null,
    desc: null,
    priceMultiplier: 1,
    contractSize: 1,
    tickSize: 1,
    tickValue: 1,
    pnlCurrency: null,
    isActive: true,
  }
  addBasketIndexModalVisible.value = true
}
const formatDateTime = (security) => {
  const format = (val) => (val ? dayjs(dayjs(val).format('YYYY-MM-DD')) : null)

  security.maturityDate = format(security.maturityDate)

  return security
}

const showEditModal = async (event) => {
  const data = event?.data || event
  const params = { securityId: data.securityId }
  try {
    const resp = await security.getSecurity(params)
    if (resp.data) {
      addForm.value = formatDateTime(resp.data)
      isEdit.value = true
      addBasketIndexModalVisible.value = true
    }
  } catch (error) {
    message.error(getErrorMessage(error, 'Failed to fetch security details'))
  }
}

const handleUpdateSecurity = async (newSecurity, isEdit) => {
  loading.value = true
  try {
    if (isEdit) {
      await security.updateSec(newSecurity)
      message.success('Security updated successfully')
      return
    }
    await security.createSec(newSecurity)
    message.success('Security created successfully')
  } catch (error) {
    message.error(
      getErrorMessage(
        error,
        isEdit ? 'Failed to update security' : 'Failed to create security'
      )
    )
  } finally {
    loading.value = false
  }
}

const showCopyModal = async () => {
  const selectedRows = gridApi.value?.getSelectedRows?.() || []
  if (selectedRows.length !== 1) {
    message.warning('Please select one row to copy')
    return
  }

  const params = { securityId: selectedRows[0].securityId }
  try {
    const resp = await security.getSecurity(params)
    if (resp.data) {
      const data = resp.data
      addForm.value = formatDateTime({
        ...data,
        securityId: undefined,
        externalSecurityId: undefined,
        exchange: { ...data.exchange },
        issuer: { ...data.issuer },
        identifiers: data.identifiers ? [...data.identifiers] : [],
      })
      isEdit.value = false
      addBasketIndexModalVisible.value = true
    }
  } catch (error) {
    message.error(getErrorMessage(error, 'Failed to fetch security details'))
  }
}

const filterOption = (input, option) => {
  const key = option?.key ?? option?.value ?? ''
  return String(key).toLowerCase().includes(input.toLowerCase())
}

const showIdentifierModal = async (event) => {
  const data = event?.data || event
  const params = { securityId: data.securityId }
  try {
    const resp = await security.getSecurity(params)
    identifierRawData.value.securityId = data.securityId
    identifierRawData.value.identifiers = resp.data.identifiers || []
    identifierModalVisible.value = true
  } catch (error) {
    message.error(getErrorMessage(error, 'Failed to fetch security details'))
  }
}

const handleUpdateIdentifiers = () => {}

const columnDefs = computed(() =>
  buildInterestRateColumnDefs({
    onIdentifiers: showIdentifierModal,
    onEdit: showEditModal,
  })
)

onMounted(async () => {
  try {
    const result = await reference.getReferenceDataV2()
    exchangeList.value = result.data?.exchanges || []
    identifierTypeList.value = result.data?.identifierTypes || []
  } catch (e) {
    message.error(getErrorMessage(e, 'Failed to fetch reference lists'))
  }
})

provide('exchangeList', exchangeList)
provide('typeCodeOptions', typeCodeOptions)
provide('termCodeOptions', termCodeOptions)
provide('identifierTypeList', identifierTypeList)
provide('security', security)
</script>
